/**
 * ImageCollectionFilters Component
 *
 * Filter sidebar for Image Collection.
 * Provides tag, source, channel, and status filters.
 */
import { Badge } from '@/components/v2/ui';
import { Button } from '@/components/v2/ui';
import { X, Filter } from 'lucide-react';
import type { CollectionFilters } from '@/types/image-collection';

interface ImageCollectionFiltersProps {
  filters: CollectionFilters;
  filterOptions?: {
    tags: Array<{ id: string; name: string; color: string }>;
    channels: Array<{ id: string; channel_name: string }>;
    sources: Array<{ value: string; label: string }>;
    statuses: Array<{ value: string; label: string }>;
  };
  activeFilterCount: number;
  onUpdateFilters: (filters: Partial<CollectionFilters>) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
}

export function ImageCollectionFilters({
  filters,
  filterOptions,
  activeFilterCount,
  onUpdateFilters,
  onClearFilters,
  isLoading,
}: ImageCollectionFiltersProps) {
  const handleToggleTag = (tagId: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((id) => id !== tagId)
      : [...currentTags, tagId];

    onUpdateFilters({ tags: newTags.length > 0 ? newTags : undefined });
  };

  const handleSourceChange = (source: string) => {
    onUpdateFilters({ source: source as CollectionFilters['source'] });
  };

  const handleChannelChange = (channelId: string) => {
    onUpdateFilters({ channel: channelId });
  };

  const handleStatusChange = (status: string) => {
    onUpdateFilters({ status: status as CollectionFilters['status'] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-neutral-900 dark:border-neutral-50" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-neutral-500" />
          <h3 className="font-medium text-sm text-neutral-900 dark:text-neutral-50">
            Filter
          </h3>
          {activeFilterCount > 0 && (
            <Badge variant="default" className="text-xs">
              {activeFilterCount}
            </Badge>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="h-6 px-2 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Tags Filter */}
      {filterOptions?.tags && filterOptions.tags.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Tags
          </label>
          <div className="space-y-1">
            {filterOptions.tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleToggleTag(tag.id)}
                className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                  filters.tags?.includes(tag.id)
                    ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span>{tag.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Source Filter */}
      {filterOptions?.sources && (
        <div>
          <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Source
          </label>
          <div className="space-y-1">
            <button
              onClick={() => onUpdateFilters({ source: undefined })}
              className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                !filters.source
                  ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
              }`}
            >
              All Sources
            </button>
            {filterOptions.sources.map((source) => (
              <button
                key={source.value}
                onClick={() => handleSourceChange(source.value)}
                className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                  filters.source === source.value
                    ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {source.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Channel Filter */}
      {filterOptions?.channels && filterOptions.channels.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Channel
          </label>
          <div className="space-y-1">
            <button
              onClick={() => onUpdateFilters({ channel: undefined })}
              className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                !filters.channel
                  ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
              }`}
            >
              All Channels
            </button>
            {filterOptions.channels.map((channel) => (
              <button
                key={channel.id}
                onClick={() => handleChannelChange(channel.id)}
                className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                  filters.channel === channel.id
                    ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {channel.channel_name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status Filter */}
      {filterOptions?.statuses && (
        <div>
          <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Status
          </label>
          <div className="space-y-1">
            <button
              onClick={() => onUpdateFilters({ status: undefined })}
              className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                !filters.status
                  ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
              }`}
            >
              All Statuses
            </button>
            {filterOptions.statuses.map((status) => (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status.value)}
                className={`w-full px-2 py-1.5 rounded text-left text-xs transition-colors ${
                  filters.status === status.value
                    ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-50'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
